
import React from "react";
import { Calendar } from "lucide-react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { z } from "zod";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { athleteTierSchema } from "@/contexts/program-creation/types";

interface CompetitionScheduleSectionProps {
  form: UseFormReturn<z.infer<typeof athleteTierSchema>>;
}

export default function CompetitionScheduleSection({ form }: CompetitionScheduleSectionProps) {
  // Watch the seasonCycle value to conditionally render fields
  const seasonCycle = useWatch({
    control: form.control,
    name: "seasonCycle",
    defaultValue: "off_season"
  });

  return (
    <div className="space-y-5 mb-8">
      <div className="flex items-center gap-2 text-white/90 border-b border-white/20 pb-2">
        <Calendar size={18} />
        <h4 className="font-medium">Competition Schedule</h4>
      </div>
      
      <FormField
        control={form.control}
        name="seasonCycle"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white/90 text-sm">
              Where are they in their season or training cycle?
            </FormLabel>
            <Select 
              value={field.value} 
              onValueChange={field.onChange}
              defaultValue="off_season"
            >
              <FormControl>
                <SelectTrigger className="bg-white/20 border-white/30 text-white w-full h-12">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="off_season">Off-season</SelectItem>
                <SelectItem value="pre_season">Pre-season</SelectItem>
                <SelectItem value="in_season">In-season</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {/* Show important dates field if in pre-season or in-season */}
      {(seasonCycle === "pre_season" || seasonCycle === "in_season") && (
        <FormField
          control={form.control}
          name="importantDates"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-white/90 text-sm">
                Do they have any upcoming competitions or events? Please list dates.
              </FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="e.g., First game of season on March 1 or National championship in 6 months" 
                  className="bg-white/20 border-white/30 text-white min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
